import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Mail, MapPin } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import TopNewsBar from "./TopNewsBar";

export const NAV = [
  { label: "Home", to: "/", accent: true },
  {
    label: "About",
    to: "/about",
    items: [
      { to: "/about#about", label: "About Us" },
      { to: "/about#founder", label: "Founder" },
      { to: "/about#journey", label: "Our Journey" },
      { to: "/about#values", label: "Our Values" },
      { to: "/about#team", label: "Our Team" },
    ],
  },
  {
    label: "Causes",
    to: "/causes",
    items: [
      { to: "/causes#programs", label: "Areas of Impact" },
      { to: "/causes#works", label: "Works Under Progress" },
      { to: "/causes#gallery", label: "Before & After" },
      { to: "/causes#gadag", label: "Gadag Chapter" },
    ],
  },
  {
    label: "Pages",
    to: "/impact",
    items: [
      { to: "/impact", label: "Impact" },
      { to: "/impact#stories", label: "Stories" },
      { to: "/get-involved", label: "Get Involved" },
      { to: "/get-involved#volunteer", label: "Volunteer" },
    ],
  },
  {
    label: "Blog",
    to: "/news",
    items: [
      { to: "/news#media", label: "News" },
      { to: "/news#voices", label: "Voices" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const openDonate = () => window.dispatchEvent(new CustomEvent("open-donate", { detail: { amount: 950 } }));

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileAcc, setMobileAcc] = useState(null);
  const headerRef = useRef(null);
  const { pathname, key } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Expose the un-collapsed header height as a CSS variable so pages can offset content below the fixed header.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => {
      if (window.scrollY <= 40) {
        document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenIdx(null);
  }, [pathname, key]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to.split("#")[0]));

  return (
    <header ref={headerRef} data-testid="site-header" className="fixed top-0 left-0 right-0 z-50 w-full">
      <TopNewsBar />

      {/* Top navy contact strip */}
      <div className={`bg-navy text-white transition-all ${scrolled ? "h-0 overflow-hidden py-0" : "py-2 md:py-3"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div className="hidden md:flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center">
                <Mail className="w-4 h-4 text-teal" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/90">Email Address</div>
                <a href="mailto:connect@aksharadaana.org" className="text-sm text-white/80 hover:text-teal">connect@aksharadaana.org</a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-teal" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/90">Bengaluru HQ</div>
                <div className="text-sm text-white/80">Kothnur, Bengaluru &mdash; 560076</div>
              </div>
            </div>
          </div>
          <div className="md:hidden text-[11px] leading-tight text-white/80 min-w-0 truncate">Akshara Daana · Rural Education, Karnataka</div>
          <LanguageSwitcher variant="dark" />
        </div>
      </div>

      {/* Main white nav */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex items-stretch justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className={`flex items-center px-3 sm:px-4 ${scrolled ? "py-2" : "py-2.5 lg:py-3"} transition-all shrink-0`}
          >
            <img
              src="/images/akshara-logo.png"
              alt="Akshara Daana — Vidya Daanam Sarva Daanam Pradhanam"
              className={`transition-all ${scrolled ? "h-14 lg:h-20" : "h-16 md:h-20 lg:h-28"} w-auto object-contain`}
            />
          </Link>

          <nav className="hidden lg:flex flex-1 items-center justify-center gap-1" aria-label="Primary">
            {NAV.map((group, i) => (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => group.items && setOpenIdx(i)}
                onMouseLeave={() => setOpenIdx(null)}
              >
                <Link
                  to={group.to}
                  data-testid={`nav-group-${i}`}
                  className={`nav-link font-display font-bold text-sm px-4 flex items-center gap-1 uppercase tracking-wider ${group.accent ? "home-accent" : "text-navy"} ${isActive(group.to) ? "active" : ""}`}
                >
                  {group.label}
                  {group.items && <ChevronDown className="w-3 h-3 opacity-70" />}
                </Link>
                {group.items && openIdx === i && (
                  <div className="absolute top-full left-0 pt-1 min-w-[220px]">
                    <ul className="py-2 bg-white shadow-xl border border-navy/10 rounded-sm">
                      {group.items.map((it) => (
                        <li key={it.label}>
                          <Link
                            to={it.to}
                            data-testid={`nav-item-${slug(it.label)}`}
                            onClick={() => setOpenIdx(null)}
                            className="block w-full text-left px-5 py-2.5 text-sm font-display text-navy hover:bg-teal/10 hover:text-teal transition-colors"
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-stretch">
            <button
              data-testid="header-donate-btn"
              onClick={openDonate}
              className="bg-teal hover:bg-teal-dark transition-colors text-white font-display font-bold uppercase tracking-wider text-xs md:text-sm px-4 md:px-8 hidden sm:flex items-center shrink-0"
            >
              Make Donation
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-navy px-4 flex items-center"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden absolute left-0 right-0 top-full bg-white border-t border-navy/10 shadow-2xl overflow-y-auto"
          style={{ maxHeight: "calc(100vh - var(--header-h, 160px))" }}
        >
          <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-3" aria-label="Mobile">
            {NAV.map((group, i) => (
              <div key={group.label} className="border-b border-navy/10 last:border-b-0">
                <div className="flex items-center justify-between">
                  <Link
                    to={group.to}
                    data-testid={`mobile-nav-${slug(group.label)}`}
                    className={`flex-1 font-display font-bold uppercase tracking-widest text-sm py-3.5 ${group.accent ? "text-coral" : "text-navy"}`}
                  >
                    {group.label}
                  </Link>
                  {group.items && (
                    <button
                      data-testid={`mobile-nav-toggle-${slug(group.label)}`}
                      onClick={() => setMobileAcc(mobileAcc === i ? null : i)}
                      aria-label={`Expand ${group.label}`}
                      className="w-11 h-11 -mr-2 flex items-center justify-center text-navy/60"
                    >
                      <ChevronDown className={`w-5 h-5 transition-transform ${mobileAcc === i ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {group.items && mobileAcc === i && (
                  <ul className="pb-3 pl-3 border-l-2 border-teal/30 ml-1 mb-2">
                    {group.items.map((it) => (
                      <li key={it.label}>
                        <Link
                          to={it.to}
                          data-testid={`mobile-nav-item-${slug(it.label)}`}
                          className="block text-navy/80 font-display py-2.5 text-[15px] hover:text-teal"
                        >
                          {it.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <button
              data-testid="mobile-donate-btn"
              onClick={() => { setMobileOpen(false); openDonate(); }}
              className="mt-4 mb-2 w-full bg-teal text-white font-display font-bold uppercase tracking-wider text-sm py-3.5 rounded-sm"
            >
              Make Donation
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
